import { Contact, ContactAddress } from '@eigtech/contacts-types'
import { objHasKeys, omitEmptyStringDeep } from '@eigtech/function-utils'
import { UsStateAbbreviated } from '@eigtech/ui-shared-us-states'
import {
  ContactFormAddressField,
  ContactFormAddressTypesFieldSchema,
} from '../ContactFormSchema/AddressFieldSchema'

export function mapContactToAddresses(contact: Partial<Contact>) {
  return contact.addresses?.map((address) => {
    // some address types come in as a string that does not match the AddressType
    // schema; use the parsed result so the value is typed as the enum (not `string`).
    const parsedAddressType = ContactFormAddressTypesFieldSchema.safeParse(address?.addressType)
    return {
      city: address?.city,
      state: (address?.stateOrProvince ?? '') as UsStateAbbreviated,
      streetAddress: address?.streetAddress?.streetOrMailingAddress,
      streetAddress2: address?.otherDesignation,
      zip: address?.zipOrPostalCode,
      // `.data` is the AddressType union, but it widens to `string` in this
      // function's inferred return; pin it to the field's type.
      addressType: (parsedAddressType.success
        ? parsedAddressType.data
        : '') as ContactFormAddressField['addressType'],
      rawAddress: address.rawAddress,
    }
  })
}

export function mapAddressesToContact(addresses: ContactFormAddressField[]): ContactAddress[] {
  return addresses
    .map((address) => ({
      id: address.id,
      streetAddress: address.streetAddress
        ? { streetOrMailingAddress: address.streetAddress }
        : undefined,
      otherDesignation: address.streetAddress2,
      city: address.city,
      stateOrProvince: address.state,
      zipOrPostalCode: address.zip,
      rawAddress:
        [address.streetAddress, address.streetAddress2, address.city, address.state, address.zip]
          .filter((value) => !!value)
          .join(', ') ||
        // some contact info will come in with only a rawAddress. if the address is not updated,
        // just pass the rawAddress along to prevent breakage
        (address.rawAddress ?? ''),

      addressType: address.addressType || undefined,
      location: address.location,
    }))
    .map(omitEmptyStringDeep)
    .filter(objHasKeys)
}
